import {
  Alert, Box, Tab, Tabs, Typography,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import React from 'react';
import CustomContainer from '../CustomContainer';
import CustomTabPanel from '../CustomTabPanel';
import AlbumProgressCard from './AlbumProgressCard';
import UpgradeMembershipBanner from './UpgradeMembershipBanner';
import { PetParentAnimal } from '../../../types/PetParent';
import { GET_ALBUMS_TYPEFORM_PROGRESS } from '../../../queries';
import { hashids, WELCOME_HOME_ALBUM_ID } from '../../../utilities';
import { a11yProps, getAnimalEmoji } from '../utils';
import { useHubPetParent } from '../HubPetParentContext';
import {
  getAlbumSpecies,
  getWelcomeHomePromptsForSpecies,
  WELCOME_HOME_ALBUM_DESCRIPTION,
  WELCOME_HOME_ALBUM_TITLE,
} from './welcomeHomeAlbum';

interface AlbumProgressQuestion {
  id: string;
  hidden?: boolean | null;
}

interface AlbumProgressResponse {
  animal?: {
    id: string;
  } | null;
  answers: {
    id?: string | null;
    fieldId: string;
  }[];
}

interface AlbumsTypeformProgressData {
  albumForm?: {
    questions: AlbumProgressQuestion[];
  } | null;
  responses?: AlbumProgressResponse[];
}

const PREMIUM_BUNDLE_IDS = [3, 7, 8];

const hasPremiumOrder = (petParent: ReturnType<typeof useHubPetParent>['petParent']) => (
  petParent?.orders?.some((order) => PREMIUM_BUNDLE_IDS.includes(order.productId)) ?? false
);

const getCompletedForPet = (
  pet: PetParentAnimal,
  questions: AlbumProgressQuestion[],
  responses: AlbumProgressResponse[],
) => {
  const species = getAlbumSpecies(pet);
  const promptTemplates = getWelcomeHomePromptsForSpecies(species);
  const visibleQuestionIds = new Set(
    questions
      .filter((question) => !question.hidden)
      .slice(0, promptTemplates.length)
      .map((question) => question.id),
  );
  const answeredFieldIds = new Set<string>();

  responses
    .filter((response) => response.animal?.id === pet.id)
    .forEach((response) => {
      response.answers?.forEach((answer) => {
        if (answer.id && visibleQuestionIds.has(answer.fieldId)) {
          answeredFieldIds.add(answer.fieldId);
        }
      });
    });

  return answeredFieldIds.size;
};

function Albums() {
  const [value, setValue] = React.useState(0);

  const { petParent, loading } = useHubPetParent();
  const {
    data: albumProgressData,
    loading: albumProgressLoading,
    error: albumProgressError,
  } = useQuery<
    AlbumsTypeformProgressData,
    { typeformFormId: string; ppId: string }
  >(GET_ALBUMS_TYPEFORM_PROGRESS, {
    variables: {
      typeformFormId: WELCOME_HOME_ALBUM_ID || '',
      ppId: petParent?.id || '',
    },
    skip: !petParent?.id || !WELCOME_HOME_ALBUM_ID,
  });

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  if (loading || albumProgressLoading) {
    return (
      <CustomContainer sx={{ pt: 4 }}>
        <Typography>Loading...</Typography>
      </CustomContainer>
    );
  }

  const pets = petParent?.animals || [];

  if (!loading && pets.length === 0) {
    return null;
  }

  if (!WELCOME_HOME_ALBUM_ID) {
    return (
      <CustomContainer sx={{ pt: 4 }}>
        <Alert severity="error">Welcome Home album is not configured</Alert>
      </CustomContainer>
    );
  }

  if (albumProgressError) {
    return (
      <CustomContainer sx={{ pt: 4 }}>
        <Alert severity="error">Unable to load albums</Alert>
      </CustomContainer>
    );
  }

  const questions = albumProgressData?.albumForm?.questions || [];
  const responses = albumProgressData?.responses || [];
  const showUpgradeBanner = !hasPremiumOrder(petParent);

  return (
    <>
      <CustomContainer sx={{ pt: 3, pb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="sectionTitle">Albums</Typography>
          <Typography variant="body2">{`Celebrate ${pets.length > 1 ? 'your pets' : pets[0].name} by filling their albums with memories.`}</Typography>
        </Box>
      </CustomContainer>

      {pets.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <CustomContainer>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="pet's albums tabs"
            >
              {pets.map((pet: PetParentAnimal, index: number) => (
                <Tab
                  key={pet.id}
                  label={`${getAnimalEmoji(pet.animalType)} ${pet.name}`}
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>
          </CustomContainer>
        </Box>
      )}

      <Box sx={{ background: '#F5F5F5', pb: 4 }}>
        {pets.map(
          (pet: PetParentAnimal, index: number) => value === index && (
          <CustomTabPanel
            key={pet.id}
            label={`albums-${pet.name.toLowerCase()}`}
          >
            {showUpgradeBanner && (
              <CustomContainer sx={{ pt: 5 }}>
                <UpgradeMembershipBanner />
              </CustomContainer>
            )}
            <CustomContainer sx={{ pt: 5 }}>
              <Box
                sx={{
                      display: 'grid',
                      gap: 3,
                      gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                    }}
              >
                <AlbumProgressCard
                  albumId={`welcome-home-${getAlbumSpecies(pet)}`}
                  petId={hashids.encode(pet.id)}
                  title={WELCOME_HOME_ALBUM_TITLE}
                  description={WELCOME_HOME_ALBUM_DESCRIPTION.replace(/\[pet name\]/g, pet.name)}
                  completed={getCompletedForPet(pet, questions, responses)}
                  total={getWelcomeHomePromptsForSpecies(getAlbumSpecies(pet)).length}
                  category="welcome-home"
                />
              </Box>
            </CustomContainer>
          </CustomTabPanel>
            ),
        )}
      </Box>
    </>
  );
}

export default Albums;
